<script src="../../../../store/state.js"></script>
<template>
  <div>
    <span class="heading-style">{{$t('general.details')}}</span>
    <form ref="form" @submit.prevent="submitMetadata">
      <div class="primary-section">
        <label class="uk-form-label" for="resource-title">{{$t('uploadDetailsInput.resourceTitle')}}</label>
        <div class="uk-form-controls">
          <input @input="resetError(inputs.title)"
                 class="uk-input input-fields"
                 v-model="resource.title"
                 :placeholder="$t('uploadDetailsInput.titlePlaceholder')"
                 title="resource-title"
                 id="resource-title"
                 maxlength='250'
                 type="text"
                 :class="{ 'uk-form-danger': submitted && errors.title }"
                 v-on:keydown.enter.prevent>
          <input-validation-message :submitted="submitted"
                                    :error="errors.title"
                                    :message="$t('uploadDetailsInput.message.title')">

          </input-validation-message>
        </div>
      </div>
      <div class="primary-section">
        <label class="uk-form-label" for="description">{{$t('general.description')}}
          <a class="optional-label">
            {{$t('general.descriptionLimitText')}}
          </a>
        </label>
        <div class="uk-form-controls">
          <description-input
            id="description"
            title="description"
            :value="resource.description"
            :placeholder="$t('uploadDetailsInput.descriptionPlaceholder')"
            :submitted="submitted"
            :error="errors.description"
            :message="$t('uploadDetailsInput.message.description')"
            v-on:onInput="updateDescription"
            v-on:resetError="resetError(inputs.description)">
          </description-input>
        </div>
      </div>
      <div class="primary-section">
        <label class="uk-form-label" for="keywords">{{$t('general.keywords')}} <a class="optional-label">
          {{$t('general.optional')}}</a></label>
        <div class="uk-form-controls">
          <keywords id="keywords"
                    maxlength='50'
                    v-on:keywordsInput="keywordsUpdate"
                    :suggestions="suggestionsList"
                    v-on:onChange="keywordsInputChange"
                    :limit="keywordsLimit"
                    :selectedKeywords="selectedKeywords"
                    :placeholder="$t('uploadDetailsInput.keywordsPlaceholder')">
          </keywords>
        </div>
      </div>
      <div class="sub-heading uk-flex uk-flex-column">
        <div>
          <span>{{$t('general.recommendations')}}</span>
        </div>
        <div class="hr">
          <hr class="float-left">
        </div>
      </div>
      <label v-if="defaultLanguage" class="uk-form-label" for="for-grades">{{$t('uploadDetailsInput.gradesTitle')}}</label>
      <label v-else class="uk-form-label" for="for-grades">{{$t('uploadDetailsInput.agesTitle')}}</label>
      <div class="uk-form-controls drop-down-width">
        <auto-complete-dropdown id="for-grades"
                                :options="getGrades"
                                :placeholder="defaultLanguage ? $t('uploadDetailsInput.gradesPlaceholder') : $t('uploadDetailsInput.agesPlaceholder')"
                                v-on:multiCheckList="updateGrades"
                                :submitted="submitted"
                                :error="errors.grades"
                                :message="$t('uploadDetailsInput.message.grades')"
                                :limit="gradesLimit"
                                :multi-selection="true"
                                :selected="selectedGrades">
        </auto-complete-dropdown>
      </div>
      <label class="uk-form-label" for="for-subjects">{{$t('uploadDetailsInput.subjectsTitle')}}</label>
      <div class="uk-form-controls drop-down-width ">
        <auto-complete-dropdown id="for-subjects"
                                :options="getSubjects"
                                :placeholder="$t('uploadDetailsInput.subjectsPlaceholder')"
                                v-on:multiCheckList="updateSubjects"
                                :submitted="submitted"
                                :error="errors.subjects"
                                :message="$t('uploadDetailsInput.message.subjects')"
                                :limit="subjectsLimit"
                                :multi-selection="true"
                                :selected="selectedSubjects">
        </auto-complete-dropdown>
      </div>
      <label class="uk-form-label" for="resource-type">{{$t('general.resourceType')}}<a
        class="optional-label"> {{$t('general.optional')}}</a></label>
      <div class="uk-form-controls drop-down-width ">
        <auto-complete-dropdown id="resource-type"
                                :options="getContentTypes"
                                :placeholder="$t('uploadDetailsInput.resourceTypePlaceholder')"
                                v-on:multiCheckList="updateResourceTypes"
                                :limit="contentTypesLimit"
                                :multi-selection="true"
                                :selected="selectedResourceTypes">
        </auto-complete-dropdown>
      </div>
      <label class="uk-form-label" for="country">{{$t('general.country')}}</label>
      <div class="uk-form-controls single-selection-padding drop-down-width ">
        <auto-complete-dropdown id="country"
                                :options="getCountries"
                                :placeholder="$t('uploadDetailsInput.countryPlaceholder')"
                                v-on:multiCheckList="updateCountry"
                                :submitted="submitted"
                                :error="errors.country"
                                :message="$t('uploadDetailsInput.message.country')"
                                :multiSelection="false"
                                :selected="selectedCountry">
        </auto-complete-dropdown>
      </div>
      <label class="uk-form-label" for="language">{{$t('general.language')}}</label>
      <div class="uk-form-controls single-selection-padding drop-down-width">
        <auto-complete-dropdown id="language"
                                :options="getLanguages"
                                :placeholder="$t('uploadDetailsInput.languagePlaceholder')"
                                v-on:multiCheckList="updateLanguage"
                                :submitted="submitted"
                                :error="errors.language"
                                :message="$t('uploadDetailsInput.message.language')"
                                :multiSelection="false"
                                :selected="selectedBrowserLanguage">
        </auto-complete-dropdown>
      </div>
      <label class="uk-form-label" for="standard">{{$t('general.standard')}}<a
        class="optional-label"> {{$t('general.optional')}}</a></label>
      <div class="uk-form-controls single-selection-padding drop-down-width" >
        <auto-complete-dropdown id="standard"
                                :options="getStandards"
                                :placeholder="$t('uploadDetailsInput.standardsPlaceholder')"
                                v-on:multiCheckList="updateStandard"
                                :submitted="submitted"
                                :multiSelection="false"
                                :selected="selectedStandard">
        </auto-complete-dropdown>
      </div>

      <div v-if="resource.standard.length">
        <label class="uk-form-label" for="strands">{{$t('general.strands')}}</label>
        <div class="uk-form-controls drop-down-width">
          <strands-dropdown :clearSelection="clearStrands"
                            :error="errors.strands"
                            :fetch-complete="fetchComplete"
                            :limit="strandsLimit"
                            :message="$t('uploadDetailsInput.message.strands')"
                            :multiSelection="true"
                            :options="resolveFilteredStrands"
                            :placeholder="$t('uploadDetailsInput.strandsPlaceholder')"
                            :selected="selectedStrands"
                            :submitted="submitted"
                            id="strands"
                            v-on:multiCheckList="updateStrands"
                            v-on:strandSearch="getStrands"
                            v-on:defaultStrandSearch="defaultSearch"
                            v-on:strandSelected="updateStrands">
          </strands-dropdown>
        </div>
      </div>


      <div class="uk-form-controls">
        <input-validation-message :submitted="submitted"
                                  :error="errors.terms"
                                  :message="$t('uploadDetailsInput.message.acceptTerms')">
        </input-validation-message>
        <div class="uk-flex">
          <div class="conditions-checkbox-container">
            <img class="checkbox"
                 v-if="!errors.terms"
                 :class="{'empty-checkbox': !resource.terms,'errors-checkbox': !errors.terms}"
                 :src="activeCheckbox"
                 @click="updateTerms(false)"
                 v-on:keydown.enter="updateTerms(false)"
                 tabindex="0">
            <img class="checkbox"
                 v-if="!errors.terms"
                 :class="{'empty-checkbox': resource.terms,'errors-checkbox': !errors.terms}"
                 :src="emptyCheckBox"
                 @click="updateTerms(true)"
                 v-on:keydown.enter="updateTerms(true)"
                 tabindex="0">
            <img class="checkbox"
                 v-if="errors.terms"
                 :src="errorsCheckbox"
                 @click="updateTerms(true)"
                 v-on:keydown.enter="updateTerms(true)"
                 tabindex="0">
          </div>
          <div class="conditions-label-container">
            <label class="conditions-label">
              <span class="link" v-html="$t('uploadDetailsInput.compliance')"></span>
            </label>
          </div>
        </div>
        <div class="submit-button-container"
             tabindex="0"
             v-on:keydown.enter="submitMetadata">
          <input ref="submit-button"
                 type="submit"
                 :value="$t('general.share')"
                 name="submit-button"
                 class="uk-button"
                 :class="{'submit-button': !shareDisabled, 'disabled-button': shareDisabled}"
                 :disabled="shareDisabled">
        </div>
      </div>
    </form>
  </div>
</template>

<script>
  import inputValidation from './../../../../utils/upload-input-validation-util'
  import autoCompleteDropdown from '../../../ui-components/autocomplete-dropdown/autocomplete-dropdown'
  import strandsDropdown from '../../../ui-components/strands-dropdown/strands-dropdown'
  import keywords from '../../../ui-components/keywords-input/keywords-input'
  import {mapActions, mapGetters, mapMutations} from 'vuex'
  import iconUtil from '../../../../utils/icon-util'
  import * as misc from '../../../../enums/misc'
  import * as inputValidationEnum from '../../../../enums/input-validation'
  import * as moderationStatus from '../../../../enums/moderation-status'
  import inputValidationMessage from '../../../ui-components/validation/input-validation-message'
  import * as staticUrls from '../../../../enums/static-urls'
  import * as _ from 'lodash'
  import * as event from '../../../../enums/mixpanel-event'
  import metrics from '../../../../services/metrics'
  import gradeUtil from '../../../../utils/grade-util'
  import gradeIdUtil from '../../../../utils/grade-id-util'
  import ageUtil from '../../../../utils/age-util'
  import subjectUtil from '../../../../utils/subject-translator-util'
  import resourceUtil from '../../../../utils/resource-type-util'
  import * as idStore from '../../../../enums/id-store-keys'
  import Router from '../../../../router'
  import * as countryCode from '../../../../enums/supported-countries'
  import * as languageCode from '../../../../enums/supported-languages'
  import mixpanel from '../../../../services/mixpanel'
  import descriptionInput from '../../../ui-components/inputs/description-input'
  import userLanguageUtil from '../../../../utils/user-languages-util'
  import edgate from '../../../../services/smart-edgate'

  export default {
    title: 'upload-details-input',
    components: {
      strandsDropdown,
      autoCompleteDropdown,
      keywords,
      inputValidationMessage,
      descriptionInput
    },
    props: ['contentId', 'fileName', 'metadata', 'shareDisabled', 'userLocation'],
    data() {
      return {
        inputs: inputValidationEnum.inputs,
        resource: {
          title: '',
          description: '',
          keywords: [],
          grades: [],
          subjects: [],
          types: [],
          terms: false,
          author: '',
          country: '',
          language: '',
          standard: [],
          strands: []
        },
        submitted: false,
        errors: {
          title: false,
          description: false,
          grades: false,
          subjects: false,
          terms: false,
          country: false,
          language: false,
          strands: false
        },
        emptyCheckBox: iconUtil.resolve(misc.LRG_UNCHECKED),
        activeCheckbox: iconUtil.resolve(misc.LRG_CHECKED),
        errorsCheckbox: iconUtil.resolve(misc.LRG_ERRORS_UNCHECKED),
        inputValidation: this.$t('uploadDetailsInput.message'),
        legalTerms: staticUrls.UPLOAD_LEGAL_TERMS,
        legalCommons: staticUrls.LEGAL_COMMONS,
        suggestionsList: [],
        gradesLimit: 15,
        subjectsLimit: 10,
        keywordsLimit: 10,
        contentTypesLimit: 7,
        strandsLimit: 10,
        filteredStrands: [],
        clearStrands: false,
        fetchComplete: false
      }
    },
    methods: {
      ...mapActions(['putMetaData', 'fetchIDData', 'storeIDData', 'initResourceMetadata', 'resolveStrandData']),
      ...mapMutations(['setPendingModerationResourceDefaults', 'setApprovedModerationResourceDefaults', 'setDeclinedModerationResourceDefaults', 'setSelectedModeratedResource', 'setLastPulledDate']),
      submitMetadata() {
        if (!this.shareDisabled) {
          if (!this.metadata) {
            this.initializeResourceMetadata()
          } else {
            this.updateResourceMetadata()
          }
        }
      },
      getStrands(q) {
        if (q) {
          this.resolveFetchComplete(false)
          this.filteredStrands = []
          this.debounceSearch(q, this.resolveGrades(), this.resource.standard[0], this.resolveStrands, this.resolveFetchComplete)
        }
      },
      defaultSearch(q) {
        this.resolveFetchComplete(false)
        this.filteredStrands = []
        this.debounceSearch(q, this.resolveGrades(), this.resource.standard[0], this.resolveStrands, this.resolveFetchComplete)
      },
      resolveStrands(strand) {
        this.filteredStrands.push(strand)
      },
      resolveFetchComplete(complete) {
        this.fetchComplete = complete
      },
      debounceSearch: _.debounce((q, grades, std, resolveStrands, resolveFetchComplete) => {
        edgate.search(null, grades, std, q)
                .then(response => {
                  let json = JSON.parse(response.text)
                  for (let result of json.results) {
                    let strand = {
                      title: result.standard_number + '  ' + result.standard_text,
                      id: result.guid,
                      standard: result.standard_number,
                      description: result.standard_text
                    }
                    resolveStrands(strand)
                  }
                  resolveFetchComplete(true)
                }).catch(() => {
                  resolveFetchComplete(true)
                })
      }, 1000),
      updateResourceMetadata() {
        this.submitted = true
        this.resource.author = this.getUserProfile.userName
        let errorsArr = inputValidation.validate(this.resource, this.errors)
        if (!errorsArr.includes(true)) {
          this.putMetaData({
            metadata: this.resource,
            contentId: this.contentId,
            filters: {
              grades: this.getGrades,
              subjects: this.getSubjects,
              contentTypes: this.getContentTypes
            }
          }).then(() => {
            this.trackAction()
            this.setPendingModerationResourceDefaults()
            this.setApprovedModerationResourceDefaults()
            this.setDeclinedModerationResourceDefaults()
            this.$emit('upload-complete')
            this.setSelectedModeratedResource(moderationStatus.PENDING)
            let successAlert = {success: true}
            Router.push({
              name: 'ManageMyResources',
              params: {metadata: successAlert}
            })
          })
        }
      },
      initializeResourceMetadata() {
        this.submitted = true
        this.resource.author = this.getUserProfile.userName
        let errorsArr = inputValidation.validate(this.resource, this.errors)
        if (!errorsArr.includes(true)) {
          this.initResourceMetadata({
            metadata: this.resource,
            contentId: this.contentId,
            filters: {
              grades: this.getGrades,
              subjects: this.getSubjects,
              contentTypes: this.getContentTypes
            }
          }).then(() => {
            this.trackAction()
            this.resolveShareComplete()
            this.setPendingModerationResourceDefaults()
            this.setApprovedModerationResourceDefaults()
            this.setDeclinedModerationResourceDefaults()
            this.$emit('upload-complete')
            this.setSelectedModeratedResource(moderationStatus.PENDING)
            let successAlert = {success: true}
            Router.push({
              name: 'ManageMyResources',
              params: {metadata: successAlert}
            })
          })
        }
      },
      resolveShareComplete() {
        let keyFound = false
        this.fetchIDData()
          .then(result => {
            if (!_.isEmpty(result.body)) {
              for (let key in result.body) {
                if (key === idStore.SHARE_COMPLETE) {
                  keyFound = true
                  if (result.body[key] !== 'true') {
                    this.storeIDData({'shareComplete': true})
                  }
                }
              }
              if (!keyFound) {
                this.storeIDData({'shareComplete': true})
              }
            } else {
              this.storeIDData({'shareComplete': true})
            }
          })
          .catch(err => {
            this.error = err
            metrics.sendMetric(metrics.CATEGORIES.THIRD_PARTY_CALLS, metrics.CP_TAGS.FETCH_ID_STORE_ERROR, 1, metrics.TYPE.FAILURE_COUNTER, err)
          })
      },
      trackAction() {
        if (!this.metadata) {
          mixpanel.track(event.FILE_SHARED)
        } else {
          mixpanel.track(event.FILE_EDITED)
        }
      },
      resetError(key) {
        this.errors[key] = false
      },
      updateGrades(selectedGrades) {
        this.resource.grades = selectedGrades
        this.errors['grades'] = false
      },
      updateSubjects(selectedSubjects) {
        this.resource.subjects = selectedSubjects
        this.errors['subjects'] = false
      },
      updateStandard(standard) {
        let id = this.getStandards.find(x => x.title === standard[0]).id
        if (id !== 'none') {
          this.resource.standard = []
          this.resource.standard.push(id)
          this.defaultSearch(1)
        } else {
          this.resource.standard = []
          this.resource.strands = []
          this.errors.strands = false
        }
        this.filteredStrands = []
        this.resolveClearStrands()
      },
      resolveClearStrands() {
        this.clearStrands = true
        setTimeout(() => {
          this.clearStrands = false
        }, 250)
      },
      updateStrands(selectedStrands) {
        this.resource.strands = selectedStrands
        this.errors['strands'] = false
      },
      updateResourceTypes(selectedResourceTypes) {
        this.resource.types = selectedResourceTypes
      },
      updateDescription(value) {
        this.resource.description = value
      },
      keywordsUpdate(keywords) {
        this.resource.keywords = keywords
      },
      updateTerms(active) {
        this.resource.terms = active
        this.errors.terms = !active
      },
      updateLanguage(language) {
        let supportedLanguages = this.$t('languages')
        let languageTitle = Object.keys(supportedLanguages).find(key => supportedLanguages[key] === language[0])
        this.resource.language = languageCode.SUPPORTED_LANGUAGES.find(o => o.title === languageTitle).ID
        this.errors.language = false
      },
      updateCountry(country) {
        let supportedCountries = this.$t('countries')
        let countryTitle = Object.keys(supportedCountries).find(key => supportedCountries[key] === country[0])
        this.resource.country = countryCode.SUPPORTED_COUNTRIES.find(o => o.title === countryTitle).ID
        this.errors.country = false
      },
      keywordsInputChange(searchText) {
        this.suggestionsList = _.values(this.$t('suggestions'))
      },
      redirect(url, target) {
        window.open(url, target)
      },
      resolveGrades() {
        let grades = []
        for (let grade of this.resource.grades) {
          let gradeId = gradeIdUtil.resolve(grade)
          if (gradeId !== '-1' && gradeId !== '13') {
            grades.push(gradeId)
          }
        }
        return grades.length >= 1 ? grades : null
      }
    },
    computed: {
      ...mapGetters('auth/id', ['authenticated']),
      ...mapGetters(['getSubjects', 'getGrades', 'getContentTypes', 'getUserProfile', 'getCountries', 'getLanguages', 'getStandards']),
      resolveFilteredStrands() {
        return _.uniqBy(this.filteredStrands, 'title')
      },
      selectedKeywords() {
        try {
          if (this.metadata.keywords) {
            return this.metadata.keywords
          }
        } catch (err) {
          // log error
        }
      },
      selectedGrades() {
        try {
          if (this.metadata.grades[0].levels) {
            let grades = []
            for (let grade of this.metadata.grades[0].levels) {
              if (this.defaultLanguage) {
                grades.push(gradeUtil.resolve(grade))
              } else {
                grades.push(ageUtil.resolve(grade, true))
              }
            }
            return grades
          }
        } catch (err) {
          // log error
        }
      },
      selectedSubjects() {
        try {
          let metadataSubjects = []
          if (this.metadata) {
            for (let subject of this.metadata.subjects) {
              metadataSubjects.push(subjectUtil.resolveToMasterSubjectList(subject, this.$t('subjects')))
            }
            return [...new Set(metadataSubjects)]
          }
        } catch (err) {
          // log error
        }
      },
      selectedResourceTypes() {
        try {
          if (this.metadata.resourceTypes) {
            let resourceTypes = []
            for (let type of this.metadata.resourceTypes) {
              resourceTypes.push(resourceUtil.resolve(type, this.$t('resourceTypes')))
            }
            return resourceTypes
          }
        } catch (err) {
          // log error
        }
      },
      selectedStandard() {
        try {
          if (this.metadata.standardSets) {
            let standard = []
            standard.push(this.getStandards.find(x => x.id === this.metadata.standardSets[0]).title)
            return standard
          }
        } catch (err) {
           // log error
        }
      },
      selectedStrands() {
        try {
          if (this.metadata.standardStrands) {
            return this.metadata.standardStrands
          }
        } catch (err) {
           // log error
        }
      },
      selectedBrowserLanguage() {
        // Authenticated check to stop automatic selection in case user needs to sign in first
        if (this.authenticated) {
          let supportedLanguages = this.$t('languages')
          if (this.metadata && this.metadata.language) {
            let supportedLanguagesArr = []
            let languageTitle = languageCode.SUPPORTED_LANGUAGES.find(o => o.ID === this.metadata.language).title
            supportedLanguagesArr.push(supportedLanguages[languageTitle])
            return supportedLanguagesArr
          } else {
            let language = userLanguageUtil.resolve()
            let languageTitle = languageCode.SUPPORTED_LANGUAGES.find(o => o.ID === language[0]).title
            this.resource.language = language[0]
            return [supportedLanguages[languageTitle]]
          }
        }
      },
      selectedCountry() {
        // Authenticated check to stop automatic selection in case user needs to sign in first
        if (this.authenticated) {
          let supportedCountries = this.$t('countries')
          let countryArr = []
          let countryTitle = ''
          if (this.metadata && this.metadata.country) {
            countryTitle = countryCode.SUPPORTED_COUNTRIES.find(o => o.ID === this.metadata.country).title
            countryArr.push(supportedCountries[countryTitle])
          } else {
            if (this.userLocation) {
              try {
                // England, Scotland, Northern Ireland & Wales fall under 1 country code: GB.
                // Using the region name to differentiate between them
                if (this.userLocation.countryCode !== 'GB') {
                  countryTitle = countryCode.SUPPORTED_COUNTRIES.find(o => o.countryCode === this.userLocation.countryCode).title
                  countryArr.push(supportedCountries[countryTitle])
                } else {
                  countryTitle = countryCode.SUPPORTED_COUNTRIES.find(o => o.regionName === this.userLocation.regionName).title
                  countryArr.push(supportedCountries[countryTitle])
                }
              } catch (err) {
                metrics.sendMetric(metrics.CATEGORIES.THIRD_PARTY_CALLS, metrics.CP_TAGS.UNSUPPORTED_LOCATION, 1, metrics.TYPE.FAILURE_COUNTER, err)
              }
            }
          }
          return countryArr
        }
      },
      defaultLanguage() {
        return this.$i18n.locale === 'en' || this.$i18n.locale === 'en-US'
      }
    },
    watch: {
      fileName(value) {
        this.resource.title = value || ''
      },
      resource: {
        handler(resource) {
          try {
            let keyCount = 0
            for (let key in resource) {
              if (key !== 'terms') {
                if (resource[key].length) {
                  keyCount++
                }
              }
            }
            this.$emit('metadata-input-count', keyCount)
          } catch (err) {
          }
        },
        deep: true
      }
    },
    created() {
      if (this.metadata) {
        this.resource.title = this.metadata.title
        this.resource.description = this.metadata.description
        this.resource.keywords = this.metadata.keywords
        this.resource.country = this.metadata.country
        this.resource.language = this.metadata.language
      }
    }
  }
</script>

<style scoped lang="scss">
  @import '../../../../styles/colours';
  @import '../../../../styles/breakpoints';

  .float-left {
    float: left;
  }

  .hr {
    padding-top: 12px;
  }

  .conditions-checkbox-container {
    width: 26px;
  }

  label {
    padding-bottom: 12px;
  }

  form {
    padding-top: 24px;
  }

  hr {
    width: 499px;
    height: 8px;
    opacity: 0.8;
    border-width: 3px;
  }

  .heading-style {
    font-size: 30px;
    font-weight: bold;
    color: $content-text;
  }

  .uk-form-danger {
    color: $input-warning;
    border-color: $input-warning;
  }

  .input-fields {
    width: 500px;
    border-radius: 1px;
    box-shadow: $input-shadow;
    background-color: white;
    font-size: 18px;
    color: $share-input-text;
    padding-left: 13px;
    &:hover, &:focus {
      border: 2px solid $light-blue;
      box-shadow: 3px 5px 8px 0 rgba(0, 0, 0, 0.24);
    }
  }

  .input-fields::placeholder {
    font-style: italic;
    font-size: 16px;
    color: $slate;
  }

  .input-fields:-ms-input-placeholder {
    font-style: italic !important;
    font-size: 16px !important;
    color: $slate !important;
  }

  .input-fields::-ms-input-placeholder {
    font-style: italic;
    font-size: 16px;
    color: $slate;
  }

  textarea {
    height: 150px;
    padding: 15px 15px 0px 13px;
    resize: none;
    overflow-y: scroll;
  }

  .conditions-label-container {
    width: 474px;
    height: 66px;
    padding-bottom: 32px;
    display: flex;
    margin-left: -10px;
  }

  .conditions-label {
    font-size: 15px;
    line-height: 1.33;
    color: $content-text;
    padding: 0px 0px 0px 10px;
  }

  .submit-button-container {
    max-width: 500px;
    height: 64px;
  }

  .submit-button {
    width: 100%;
    height: 100%;
    border-radius: 2px;
    box-shadow: 0 1px 3px 0 $drop-shadow-black;
    background-color: $light-blue;
    font-size: 24px;
    color: white;
    &:hover {
      background-color: $button-hover;
      color: $light-blue;
      box-shadow: 3px 5px 8px 0 rgba(0, 0, 0, 0.24);
    }
  }

  .disabled-button {
    width: 100%;
    height: 100%;
    font-size: 24px;
    color: $light-grey;
    border-radius: 2px;
    border: solid 2.8px $light-grey;
    background-color: $light-gray;
  }

  .uk-form-label {
    font-size: 18px;
    color: $content-text;
  }

  .optional-label {
    color: $text-area-background;
    font-style: italic;
    text-decoration: none;
    cursor: default;
    &:hover {
      font-weight: normal;
    }
  }

  .uk-form-controls {
    padding: 12px 0px 3px 0px;
  }

  .uk-input {
    height: 46px;
  }

  .sub-heading {
    margin-top: 12px;
    font-size: 22px;
    font-weight: bold;
  }

  input.input-fields {
    height: 46px;
  }

  .primary-section {
    padding-top: 10px;
  }

  .single-selection-padding {
    padding-bottom: 20px;
  }

  .input-checkbox {
    display: none;
    left: 0px;
  }

  .empty-checkbox {
    display: none;
  }

  .checkbox {
    height: 16px;
    width: 16px;
  }

  .link {
    /deep/
    a {
      color: $content-link-blue;
      font-weight: bold;
      &:hover {
        text-decoration: underline;
      }
    }
  }

  errors-checkbox {
    display: none;
  }

  .uk-button {
    text-transform: none;
  }

  .conditions-checkbox-container {
    width: 26px;
  }

  .drop-down-width {
    max-width: 498px;
  }

  @media screen and (max-width: $breakpoint-xlarge) {
    .input-fields {
      width: 450px;
    }
    li {
      width: 450px;
    }
    .suggestion-list {
      width: 450px;
    }
    .conditions-label-container {
      width: 424px;
    }
    .submit-button-container {
      width: 450px;
    }
    hr {
      width: 450px;
    }

    .drop-down-width {
      max-width: 450px;
    }
  }
</style>
